import React, { useMemo, useState } from "react";
import styles from "./QuestProgressCard.module.scss";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import type { QuestWithProgress } from "@/hooks/useUserQuestStats";
import { useGetQuestConfigsForUser } from "@/hooks/useGetQuestConfigsForUser";
import { useGetAllQuestConfigsForUser } from "@/hooks/useGetAllQuestConfigsForUser";
import { useGetQuestProgressionForUser } from "@/hooks/useGetQuestProgressionForUser";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton from "@/components/skeletons/Skeleton";
import { FaTrophy } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import AnimateHeight from "react-animate-height";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import getConfig from "@/utils/getConfig";
import Toast from "@/components/Toast";
import { QuestsApi, OWGameResponse, QuestConfig, QuestProgression, RewardType, AmountUnit } from "@devour/client";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import { useAllGamesList } from "@/hooks/useAllGamesQuests";
import { useGetRewardConfigs } from "@/hooks/useGetRewardConfigs";
import ClaimBadgeButton from "@/components/goVip/ClaimBadgeButton";
import useWindowSize from "@/hooks/useWindowSize";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import { NftPromoModal } from "@/components/modals/NftPromoModal";
import { useGetOverwolfSummary } from "@/hooks/useGetOverwolfSummary";

export type { QuestWithProgress } from "@/hooks/useUserQuestStats";

type FilterType = "all" | "claimable" | "in-progress" | "claimed";
type TimePeriodFilter = "all" | "daily" | "weekly" | "monthly" | "all-time";

// New Quest System Data Types
interface _QuestWithNewProgress {
    questConfig: QuestConfig;
    progression?: QuestProgression;
    gameInfo?: OWGameResponse;
    // Computed properties for UI
    totalProgress: number;
    totalTarget: number;
    percentComplete: number;
    isClaimable: boolean;
    isClaimed: boolean;
    resetType: "daily" | "weekly" | "monthly" | "never";
}

interface QuestProgressCardProps {
    onQuestsLoaded?: (quests: Array<{ quest: { id: string; name: string }; [key: string]: unknown }>) => void;
    compact?: boolean; // Force compact mode
    defaultExpanded?: boolean; // Override default expand state
    forceExpanded?: boolean; // Bypass preferences, always expanded
}

export const QuestProgressCard: React.FC<QuestProgressCardProps> = ({ onQuestsLoaded, compact = false, defaultExpanded = false, forceExpanded = false }) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const queryClient = useQueryClient();
    const { isExpanded, toggleCard } = useCockpitPreferences();
    // forceExpanded bypasses preferences entirely (used in full view mode)
    const expanded = forceExpanded || isExpanded("quest-progress", defaultExpanded);
    const [isClaimingInProgress, setIsClaimingInProgress] = useState(false);
    const [filter, setFilter] = useState<FilterType>("in-progress");
    const [timePeriodFilter, setTimePeriodFilter] = useState<TimePeriodFilter>("all");
    const [gameFilter, setGameFilter] = useState<string>("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10); // Fixed page size for client-side pagination
    const [toastMessage, _setToastMessage] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [expandedQuests, setExpandedQuests] = useState<Set<string>>(new Set());
    const [isMobile, setIsMobile] = useState(false);
    const [windowWidth] = useWindowSize();

    // NFT Promo Modal state (for claiming NFT rewards) - following GoVipOWQuestRewardCard pattern
    const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
    const [promoModalData, setPromoModalData] = useState<
        { nftUri: string; promoDescriptions: Array<string>; nftMintDate: string } | undefined
    >();

    // Toast for claim success/error (following Mission Tracker pattern)
    const [claimingToast, setClaimingToast] = useState<{ message: string; success: boolean } | null>(null);

    // Hooks for user level updates
    const { refetch: refetchUserProfile } = useGetUserProfile(fullToken);
    const { refetch: refetchUserLevel } = useGetUserLevel(fullToken, undefined);
    const { refetch: refetchSummary } = useGetOverwolfSummary();

    // Detect if we're on mobile or forced compact mode
    React.useEffect(() => {
        const checkMobile = () => {
            // If compact prop is true, always use mobile view regardless of width
            const actualIsMobile = compact || window.innerWidth <= 768;
            setIsMobile(actualIsMobile);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, [compact]);

    // Determine authentication state early (needed for hook conditions)
    const isAuthenticated = !!fullToken;

    /*
     * NEW QUEST SYSTEM: Fetch quest configs and progressions following reference pattern
     * ONLY fetch when card is expanded (lazy-loading optimization)
     */
    const shouldFetchQuestConfigs = Boolean(fullToken && gameFilter && expanded);

    /*
     * For "all games" mode, use the new all quest configs API
     * For specific games, use the existing game-specific API
     */
    const isAllGamesMode = gameFilter === "all";

    // Use new "all games" API when gameFilter is "all" AND card is expanded
    const allGamesQuery = isAllGamesMode && shouldFetchQuestConfigs ? {} : undefined;

    const { data: allQuestConfigs, isLoading: isLoadingAllQuestConfigs, refetch: refetchAllQuestConfigs } = useGetAllQuestConfigsForUser(
        fullToken,
        allGamesQuery,
    );

    /*
     * Use game-specific API when a specific game is selected (not "all")
     * This API supports both "all games" and specific game filtering
     */
    const gameSpecificQuery = !isAllGamesMode && shouldFetchQuestConfigs ? { gameId: gameFilter } : undefined;

    const { data: gameQuestConfigs, isLoading: isLoadingGameQuestConfigs, refetch: refetchGameQuestConfigs } = useGetQuestConfigsForUser(
        fullToken,
        gameSpecificQuery || { gameId: null }, // Provide default to prevent hook from being called conditionally
    );

    // Determine which quest configs to use based on mode
    const questConfigs = isAllGamesMode ? allQuestConfigs : gameQuestConfigs;
    const isLoadingQuestConfigs = isAllGamesMode ? isLoadingAllQuestConfigs : isLoadingGameQuestConfigs;

    // Only get quest IDs if we have valid quest configs AND user is authenticated
    const questConfigIds = isAuthenticated && questConfigs ? questConfigs.map(q => q.id).filter(Boolean) : [];


    const { data: questProgressions, isLoading: isLoadingQuestProgressions, refetch: refetchQuestProgressions } = useGetQuestProgressionForUser(
        fullToken,
        questConfigIds,
    );

    // Keep game list for dropdown (legacy endpoint still works for this)
    const { data: gamesList, isLoading: _loadingGames } = useAllGamesList();

    /* Collect all reward config IDs from all quests (following GoVipOWQuestRewardCard pattern) */
    const allRewardConfigIds = useMemo(() => {
        if (!questConfigs) {
            return [];
        }
        const ids: string[] = [];
        questConfigs.forEach(quest => {
            /* Add milestone reward IDs */
            const milestoneRewardIds = quest.milestones?.flatMap(m => m.rewardConfigIds || []) || [];
            ids.push(...milestoneRewardIds);
            /* Add checkpoint reward IDs (nested inside milestones) */
            quest.milestones?.forEach(milestone => {
                milestone.checkpoints?.forEach(checkpoint => {
                    if (checkpoint.rewardConfigIds) {
                        ids.push(...checkpoint.rewardConfigIds);
                    }
                });
            });
            /* Add quest-level reward IDs */
            if (quest.rewardConfigIds) {
                ids.push(...quest.rewardConfigIds);
            }
        });
        const uniqueIds = [...new Set(ids)].filter(Boolean);
        return uniqueIds;
    }, [questConfigs, gameFilter, isAllGamesMode]);

    /* Fetch all reward configs for all quests */
    const { data: rewardConfigsRes } = useGetRewardConfigs(fullToken, allRewardConfigIds);

    // Helper function to calculate XP and FUEL rewards for a specific quest
    const getQuestRewards = (questConfig: QuestConfig) => {
        if (!rewardConfigsRes?.rewards) {
            return { xpReward: 0, fuelReward: 0, xpPerRewards: [], fuelPerRewards: [] };
        }

        // Separate "per" milestone rewards from fixed rewards
        const perMilestoneRewardIds: string[] = [];
        const fixedMilestoneRewardIds: string[] = [];

        questConfig.milestones?.forEach(m => {
            const rewardIds = m.rewardConfigIds || [];
            if (m.rewardPer) {
                // This is a "per" milestone - rewards are per-action, not rolled up
                perMilestoneRewardIds.push(...rewardIds);
            } else {
                // This is a fixed milestone - rewards should be rolled up
                fixedMilestoneRewardIds.push(...rewardIds);
            }
        });

        const questRewardIds = questConfig.rewardConfigIds || [];

        // Get reward configs for each category
        const perRewards = rewardConfigsRes.rewards.filter(r =>
            perMilestoneRewardIds.includes(r.id || r._id));
        const fixedRewards = rewardConfigsRes.rewards.filter(r =>
            [...fixedMilestoneRewardIds, ...questRewardIds].includes(r.id || r._id));

        /* Calculate fixed rewards (these get rolled up) */
        const xpReward = fixedRewards
            .filter(r => r.type === RewardType.XP)
            .reduce((sum, r) => sum + (r.amount || 0), 0);

        const fuelReward = fixedRewards
            .filter(r => r.type === RewardType.FUEL)
            .reduce((sum, r) => sum + (r.amount || 0), 0);

        // Get individual "per" rewards (NOT summed - each shown separately)
        const xpPerRewards = perRewards
            .filter(r => r.type === RewardType.XP)
            .map(r => r.amount || 0);

        const fuelPerRewards = perRewards
            .filter(r => r.type === RewardType.FUEL)
            .map(r => r.amount || 0);

        return { xpReward, fuelReward, xpPerRewards, fuelPerRewards };
    };

    // Determine loading state
    const isLoading = isLoadingQuestConfigs || isLoadingQuestProgressions;

    // Create unified refetch function
    const refetch = () => {
        if (isAllGamesMode) {
            void refetchAllQuestConfigs();
        } else {
            void refetchGameQuestConfigs();
        }
        /*
         * Only refetch quest progressions if we have valid questConfigIds
         * Manual refetch calls bypass the enabled condition, so we need to check manually
         */
        if (questConfigIds && questConfigIds.length > 0) {
            void refetchQuestProgressions();
        }
    };

    /*
     * Helper function to get game name for a quest
     */
    const getGameNameForQuest = (quest: QuestConfig): string => {
        // Try to get game name from quest's game relationship
        if (quest.game?.name) return quest.game.name;
        if (quest.game?.title) return quest.game.title;

        // Fallback: try to derive from quest name or description
        const questName = quest.name?.toLowerCase() || "";
        if (questName.includes("rocket league")) return "Rocket League";
        if (questName.includes("fortnite")) return "Fortnite";
        if (questName.includes("marvel rivals")) return "Marvel Rivals";
        if (questName.includes("counter strike") || questName.includes("cs2")) return "Counter Strike 2";
        if (questName.includes("dqfe2e4")) return "DQFE2E4";

        // Final fallback
        return quest.name || "Unknown Game";
    };

    // Handle card collapse/expand
    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (e.type === "click" || (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === " ") {
            toggleCard("quest-progress");
        }
    };

    // Helper function to toggle quest expansion
    const toggleQuestExpansion = (questId: string) => {
        setExpandedQuests(prev => {
            const newSet = new Set(prev);
            if (newSet.has(questId)) {
                newSet.delete(questId);
            } else {
                newSet.add(questId);
            }
            return newSet;
        });
    };

    // Process quest data following reference implementation pattern
    const quests = useMemo(() => {
        if (!questConfigs || !questProgressions) return [];

        // Create O(1) lookup map for quest progressions - 50x faster for 50 quests
        const progressionMap = new Map(questProgressions.map(p => [p.questConfigId, p]));

        const mappedQuests = questConfigs.map((questConfig) => {
            const progression = progressionMap.get(questConfig.id); // O(1) lookup instead of O(n) find

            // Check if quest is locked (prerequisites not met) - following GameQuestSection pattern
            const prerequisiteIds = questConfig.prerequisites || [];
            const hasPrerequisites = prerequisiteIds.length > 0;
            let isLocked = false;
            if (hasPrerequisites) {
                if (!progression?.prerequisites || Object.keys(progression.prerequisites).length === 0) {
                    isLocked = true;
                } else {
                    const allMet = Object.values(progression.prerequisites).every(v => v === true);
                    if (!allMet) {
                        isLocked = true;
                    }
                }
            }

            // Calculate progress from milestones
            let totalProgress = 0;
            let totalTarget = 0;
            let hasClaimableMilestone = false;
            let claimableMilestone: any = null;
            const isComplete = progression?.isComplete ?? false;

            /*
             * Quest is considered "claimed" only if ALL milestones are complete AND claimed
             * OR if the quest root itself is claimed (for non-milestone quests)
             */
            const milestonesClaimedAndComplete = questConfig.milestones && questConfig.milestones.length > 0
                ? questConfig.milestones.every(milestone => {
                    const milestoneProgression = progression?.progression?.[milestone.id];
                    return milestoneProgression?.isComplete && milestoneProgression?.claimedAt;
                })
                : false;
            const isClaimed = (progression?.claimedAt ?? false) || milestonesClaimedAndComplete;

            if (questConfig.milestones && questConfig.milestones.length > 0) {
                questConfig.milestones.forEach(milestone => {
                    const displayDivider = milestone?.displayDivider || 1;
                    const target = Math.floor((milestone.targetAmount || 0) / displayDivider);
                    const milestoneProgression = progression?.progression?.[milestone.id];
                    if (milestoneProgression) {
                        const progress = Math.floor((milestoneProgression?.amount || 0) / displayDivider);
                        totalProgress += progress || 0;
                        totalTarget += target || 0;
                        // Check if milestone is complete but not claimed
                        if (milestoneProgression.isComplete && !milestoneProgression.claimedAt) {
                            hasClaimableMilestone = true;
                            claimableMilestone = milestone; // Store the first claimable milestone
                        }
                    } else {
                        totalTarget += target || 0;
                    }
                });
            }

            const percentComplete = totalTarget > 0 ? Math.min(totalProgress / totalTarget * 100, 100) : 0;
            const isClaimable = hasClaimableMilestone || isComplete && !isClaimed;

            // Calculate milestone info for ClaimBadgeButton (following GoVipOWQuestRewardCard pattern)
            const progressionArray = Object.values(progression?.progression || {});
            const milestonesCompleted = progressionArray.filter(p => p.isComplete).length;
            const milestonesClaimed = progressionArray.filter(p => p.claimedAt).length;
            const milestonesClaimable = questConfig.milestones.filter(m => {
                const milestoneProgression = progression?.progression?.[m.id];
                return milestoneProgression?.isComplete && !milestoneProgression?.claimedAt;
            }).length;
            const hasUnclaimedMilestones = milestonesClaimable > milestonesClaimed;
            const numOfMilestones = questConfig.milestones?.length || 0;

            // Map quest reset type
            const getResetType = (questReset: string): "daily" | "weekly" | "monthly" | "never" => {
                switch (questReset?.toUpperCase()) {
                    case "DAILY": return "daily";
                    case "WEEKLY": return "weekly";
                    case "MONTHLY": return "monthly";
                    default: return "never";
                }
            };


            /*
             * Find game info for display
             * For specific game mode, find the game from gamesList based on gameFilter
             * For "all games" mode, we need to determine the game from the quest config's game context
             * Since quest configs in "all games" mode don't necessarily include game info directly,
             * we'll use "All Games" as fallback and let the UI show quest names
             */
            const gameInfo = isAllGamesMode
                ? null // In all games mode, we show individual game names per quest
                : gamesList?.find(g => g.gameId?.toString() === gameFilter);

            // Calculate actual rewards for this quest
            const { xpReward, fuelReward, xpPerRewards, fuelPerRewards } = getQuestRewards(questConfig);

            // Return object compatible with existing filter logic
            return {
                quest: {
                    _id: questConfig.id,
                    id: questConfig.id,
                    name: questConfig.name,
                    description: questConfig.description,
                    target: totalTarget,
                    resetType: questConfig.questReset,
                    milestones: questConfig.milestones,
                    requireProgressionOrder: questConfig.requireProgressionOrder,
                },
                game: gameInfo,
                gameId: gameInfo?.gameId || (gameInfo as any)?.id,
                progress: totalProgress,
                target: totalTarget,
                isClaimable,
                isClaimed,
                percentComplete,
                type: getResetType(questConfig.questReset),
                xpReward, // Fixed rewards only (rolled up)
                fuelReward, // Fixed rewards only (rolled up)
                xpPerRewards, // Per-action rewards as array [1, 1, 2, 3] (each shown individually)
                fuelPerRewards, // Per-action rewards as array (each shown individually)
                rewardId: questConfig.id,
                canClaim: isClaimable,
                isExpired: false,
                hasRecentClaims: isClaimed,
                claimedMilestoneIndexes: [],
                hasClaimableMilestone,
                claimableMilestone, // ✅ Store the actual claimable milestone object
                resetType: questConfig.questReset,
                otherReward: null,
                // New system specific data
                questConfig,
                progression,
                // Milestone info for ClaimBadgeButton
                milestoneInfo: {
                    milestonesCompleted,
                    milestonesClaimed,
                    hasUnclaimedMilestones,
                    isMilestoneQuest: numOfMilestones > 1,
                },
                numOfMilestones,
                isLocked, // Quest is locked if prerequisites not met
            };
        });

        // In compact mode (drawer), filter out locked quests to save space
        return mappedQuests.filter(quest => !compact || !quest.isLocked);
    }, [questConfigs, questProgressions, gamesList, gameFilter, rewardConfigsRes, compact]);

    // Calculate summary statistics following the expected format
    const summary = useMemo(() => {
        const claimableQuests = quests.filter(q => q.isClaimable && !q.isClaimed);
        const inProgressQuests = quests.filter(q => !q.isClaimable && !q.isClaimed);
        const claimedQuests = quests.filter(q => q.isClaimed);

        return {
            claimableCount: claimableQuests.length,
            inProgressCount: inProgressQuests.length,
            claimedCount: claimedQuests.length,
            totalCount: quests.length,
        };
    }, [quests]);

    /*
     * Listen for Overwolf game events in the DevourPlay app
     * ow:game-events-logged fires IMMEDIATELY but backend needs time to process
     * Solution: Delay refetch by 2 seconds to let backend complete MongoDB updates
     * This is a fallback since WebSocket events don't reliably reach Overwolf iframe
     */
    useOverwolfInterop(isOnOverwolf ? (ev) => {
        if (ev.type === "ow:game-events-logged") {
            console.log("[OW_DEBUG] ow:game-events-logged fired at", new Date().toISOString(), "- waiting 2s before refetch");
            // Wait 2 seconds for backend to finish processing before refetching
            setTimeout(() => {
                console.log("[OW_DEBUG] Executing refetch after 2s delay at", new Date().toISOString());
                void refetch();
                void queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] });
                void queryClient.invalidateQueries({ queryKey: ["quest-configs-user"] });
            }, 2000);
        }
    } : undefined);


    // Game filter changes are automatically handled by the useGetQuestConfigsForUser hook

    /*
     * Quest progression updates now handled by:
     * 1. WebSocket events (line 240-242) - invalidates cache on QUESTPROGRESSED
     * 2. React Query staleTime (2 minutes) - natural cache expiration
     * 3. Overwolf interop (line 441-452) - 2s delay for backend processing
     *
     * REMOVED aggressive visibility/focus handlers that caused non-stop API calls
     * when card was expanded. Now matches GameQuestSection pattern for better performance.
     */

    // Track the previous game filter to detect changes
    const [prevGameFilter, setPrevGameFilter] = useState<string>("all");

    // Clear data when game filter changes
    React.useEffect(() => {
        if (gameFilter !== prevGameFilter) {
            setPrevGameFilter(gameFilter);
        }
    }, [gameFilter, prevGameFilter]);

    // Generate games list for dropdown filtering
    const gamesWithQuests = useMemo(() => {
        return (gamesList || [])
            .filter(game => game._id && game.name) // Ensure we have both MongoDB ID and name
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")) // Sort alphabetically
            .map(game => ({
                id: game._id || "", // Use MongoDB ObjectId for new quest system
                overwolfId: game.gameId?.toString() || "", // Keep Overwolf ID for reference
                name: game.name || "",
            }));
    }, [gamesList]);

    // Apply filtering to quests and calculate consistent counts
    const { filteredQuests, filterCounts, timePeriodCounts } = useMemo(() => {
        // Filter quests by status
        const allQuests = quests.filter(q => !q.isExpired);
        const claimableQuests = quests.filter(q => q.isClaimable && !q.isClaimed && !q.isExpired);
        /*
         * Active/In-Progress: Show quests that aren't claimed and have work left to do
         * For milestone quests: if hasUnclaimedMilestones, it's still active even if one milestone is claimable
         */
        const inProgressQuests = quests.filter(q =>
            !q.isClaimed &&
            !q.isExpired &&
            (q.milestoneInfo?.hasUnclaimedMilestones || !q.isClaimable));
        const claimedQuests = quests.filter(q => q.isClaimed);

        const counts = {
            all: allQuests.length,
            claimable: claimableQuests.length,
            "in-progress": inProgressQuests.length,
            claimed: claimedQuests.length,
        };

        let filtered;
        switch (filter) {
            case "claimable":
                filtered = claimableQuests;
                break;
            case "in-progress":
                filtered = inProgressQuests;
                break;
            case "claimed":
                filtered = claimedQuests;
                break;
            case "all":
            default:
                filtered = allQuests;
                break;
        }

        // Apply time period filter to ALL main filters (not just in-progress)
        if (timePeriodFilter !== "all") {
            filtered = filtered.filter(q => {
                const resetType = (q.type || q.quest?.resetType || q.resetType || "never").toLowerCase();
                if (timePeriodFilter === "daily") return resetType === "daily";
                if (timePeriodFilter === "weekly") return resetType === "weekly";
                if (timePeriodFilter === "monthly") return resetType === "monthly";
                if (timePeriodFilter === "all-time") return resetType === "never" || resetType === "none";
                return true;
            });
        }

        // Sort by Reset Type (Daily, Weekly, Monthly, None) then Game Name
        filtered = filtered.sort((a, b) => {
            // First sort by reset type
            const resetOrder = { "daily": 1, "weekly": 2, "monthly": 3, "never": 4, "none": 4 };
            const aReset = a.type || a.quest?.resetType || a.resetType || "never";
            const bReset = b.type || b.quest?.resetType || b.resetType || "never";
            const aOrder = resetOrder[aReset] || 5;
            const bOrder = resetOrder[bReset] || 5;

            if (aOrder !== bOrder) return aOrder - bOrder;

            // Then sort by game name
            const aGame = a.game?.name || "";
            const bGame = b.game?.name || "";
            return aGame.localeCompare(bGame);
        });

        // Calculate time period counts for current filter
        const currentFilterQuests = (() => {
            switch (filter) {
                case "claimable": return claimableQuests;
                case "in-progress": return inProgressQuests;
                case "claimed": return claimedQuests;
                case "all":
                default: return allQuests;
            }
        })();

        const periodCounts = {
            all: currentFilterQuests.length,
            daily: currentFilterQuests.filter(q => (q.type || q.quest?.resetType || q.resetType || "").toLowerCase() === "daily").length,
            weekly: currentFilterQuests.filter(q => (q.type || q.quest?.resetType || q.resetType || "").toLowerCase() === "weekly").length,
            monthly: currentFilterQuests.filter(q => (q.type || q.quest?.resetType || q.resetType || "").toLowerCase() === "monthly").length,
            "all-time": currentFilterQuests.filter(q => {
                const resetType = (q.type || q.quest?.resetType || q.resetType || "never").toLowerCase();
                return resetType === "never" || resetType === "none";
            }).length,
        };

        return { filteredQuests: filtered, filterCounts: counts, timePeriodCounts: periodCounts };
    }, [quests, filter, timePeriodFilter]);

    // Client-side pagination for filtered results
    const { paginatedQuests, pagination: clientPagination } = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredQuests.slice(startIndex, endIndex);

        const totalPages = Math.ceil(filteredQuests.length / pageSize);
        const hasMore = currentPage < totalPages;

        return {
            paginatedQuests: paginatedData,
            pagination: {
                total: filteredQuests.length,
                page: currentPage,
                totalPages,
                limit: pageSize,
                hasMore,
                offset: startIndex,
            },
        };
    }, [filteredQuests, currentPage, pageSize]);

    // Reset to page 1 when filter changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [filter, timePeriodFilter]);

    // Helper function to render progress status (reduces nested ternary complexity)
    const renderProgressStatus = (questData: QuestWithProgress) => {
        if (questData.isExpired && questData.isClaimed) {
            return <span className={styles.claimed}>Claimed (Historical)</span>;
        }
        if (questData.isClaimed) {
            return <span className={styles.claimed}>Claimed</span>;
        }
        if (questData.isExpired) {
            return <span className={styles.expired}>Expired</span>;
        }
        if (questData.isClaimable && !questData.hasClaimableMilestone) {
            return <span className={styles.complete}>Complete!</span>;
        }
        return (
            <>
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ width: `${questData.percentComplete}%` }}
                    />
                </div>
                <span className={styles.progressText}>
                    {questData.progress}/{questData.target}
                </span>
            </>
        );
    };

    // Helper function to render action buttons (uses ClaimBadgeButton from Mission Tracker)
    const _renderActionButton = (questData: QuestWithProgress) => {
        // Check if this quest has quest-level rewards (not just milestone rewards)
        const hasQuestLevelRewards = questData.questConfig?.rewardConfigIds && questData.questConfig.rewardConfigIds.length > 0;

        // If quest only has milestone rewards (no quest-level rewards), don't show quest claim button
        if (!hasQuestLevelRewards && questData.quest.milestones && questData.quest.milestones.length > 0) {
            // Show status only - milestones handle their own claim buttons
            if (questData.isClaimed || questData.milestoneInfo?.milestonesClaimed) {
                return <span className={styles.claimed}>Claimed</span>;
            }
            if (questData.percentComplete >= 80) {
                return <span className={styles.nearComplete}>Almost there!</span>;
            }
            return <span className={styles.inProgress}>Active</span>;
        }

        // For non-claimable states, show status text
        if (!questData.isClaimable && !questData.isClaimed) {
            if (questData.percentComplete >= 80) {
                return <span className={styles.nearComplete}>Almost there!</span>;
            }
            return <span className={styles.inProgress}>Active</span>;
        }

        // Use ClaimBadgeButton only for quests with quest-level rewards
        return (
            <ClaimBadgeButton
                type="quest"
                questId={questData.questConfig?.id || questData.quest?.id || ""}
                isComplete={questData.progression?.isComplete || false}
                claimedAt={questData.progression?.claimedAt}
                hasUnclaimedMilestones={questData.milestoneInfo?.hasUnclaimedMilestones || false}
                hasRootLevelRewards={hasQuestLevelRewards}
                allMilestonesClaimed={questData.milestoneInfo?.milestonesClaimed === questData.numOfMilestones && questData.numOfMilestones > 0}
                isCollapsed={false}
                onClaim={claimOverwolfReward}
                isClaimingInProgress={isClaimingInProgress}
                windowWidth={windowWidth || 1920}
                className={styles.claimButton}
            />
        );
    };

    // Helper function to render desktop action buttons (uses ClaimBadgeButton from Mission Tracker)
    const renderDesktopActionButton = (questData: QuestWithProgress) => {
        // Check if this quest has quest-level rewards (not just milestone rewards)
        const hasQuestLevelRewards = questData.questConfig?.rewardConfigIds && questData.questConfig.rewardConfigIds.length > 0;

        // If quest only has milestone rewards (no quest-level rewards), don't show quest claim button
        if (!hasQuestLevelRewards && questData.quest.milestones && questData.quest.milestones.length > 0) {
            // Show status only - milestones handle their own claim buttons
            if (questData.isClaimed || questData.milestoneInfo?.milestonesClaimed) {
                return <span className={styles.claimed}>Claimed</span>;
            }
            if (questData.percentComplete >= 80) {
                return <span className={styles.nearComplete}>Almost!</span>;
            }
            return <span className={styles.inProgress}>Active</span>;
        }

        // For non-claimable states, show status text (desktop shows different text)
        if (!questData.isClaimable && !questData.isClaimed) {
            if (questData.percentComplete >= 80) {
                return <span className={styles.nearComplete}>Almost!</span>;
            }
            return <span className={styles.inProgress}>Active</span>;
        }

        // Use ClaimBadgeButton only for quests with quest-level rewards
        return (
            <ClaimBadgeButton
                type="quest"
                questId={questData.questConfig?.id || questData.quest?.id || ""}
                isComplete={questData.progression?.isComplete || false}
                claimedAt={questData.progression?.claimedAt}
                hasUnclaimedMilestones={questData.milestoneInfo?.hasUnclaimedMilestones || false}
                hasRootLevelRewards={hasQuestLevelRewards}
                allMilestonesClaimed={questData.milestoneInfo?.milestonesClaimed === questData.numOfMilestones && questData.numOfMilestones > 0}
                isCollapsed={false}
                onClaim={claimOverwolfReward}
                isClaimingInProgress={isClaimingInProgress}
                windowWidth={windowWidth || 1920}
                className={styles.claimButton}
            />
        );
    };

    // Helper function to generate button className and avoid nested ternaries
    const getButtonClassName = (baseClass: string, isActive: boolean) => {
        return `${styles[baseClass]} ${isActive ? styles.active : ""}`;
    };

    // Helper function to generate reset period display text
    const getResetPeriodText = (type: string, isCard = false) => {
        if (type === "never") {
            return isCard ? "No Reset" : "None";
        }
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    // Notify parent component if callback provided (for backwards compatibility)
    React.useEffect(() => {
        if (onQuestsLoaded) {
            onQuestsLoaded(quests);
        }
    }, [quests, onQuestsLoaded]);

    // Helper function to format claimed rewards message (from Mission Tracker)
    const getClaimedRewardsString = (rewards: Array<{
        rewardType: RewardType;
        amount: number;
        amountUnit?: AmountUnit;
    }>) => {
        const formattedRewards = rewards.map((reward) => `${reward.amountUnit === AmountUnit.FIAT ? "$" : ""}${reward.amount} ${reward.rewardType}`);
        if (formattedRewards.length === 0) {
            return "";
        }

        if (formattedRewards.length === 1) {
            return `${formattedRewards[0]} successfully claimed!`;
        }

        if (formattedRewards.length === 2) {
            return `${formattedRewards.join(" and ")} successfully claimed!`;
        }

        const allButLast = formattedRewards.slice(0, -1);
        const last = formattedRewards[formattedRewards.length - 1];
        return `${allButLast.join(", ")}, and ${last} successfully claimed!`;
    };

    // Update user level after claiming (from Mission Tracker)
    function updateUserLevel() {
        void refetchUserProfile();
        void refetchUserLevel();
    }

    // Proven claim function from Mission Tracker (GoVipOWQuestRewardCard)
    async function claimOverwolfReward(id: string) {
        setIsClaimingInProgress(true);
        try {
            // Check if claiming an NFT to show promo modal
            const questData = quests.find(q => q.quest?.id === id || q.questConfig?.id === id);
            // Note: We'd need to fetch reward configs to check for NFT, simplified for now

            const res = await new QuestsApi(getConfig(fullToken)).createQuestConfigsIdClaimReward({
                id: id,
            });

            await refetchSummary();

            const rewardsWithoutPromo = res.rewards.filter((reward) => reward.rewardType !== RewardType.NFT);
            const rewardWithPromo = res.rewards.find((reward) => reward.rewardType === RewardType.NFT);

            if (rewardsWithoutPromo.length > 0) {
                setClaimingToast({
                    message: getClaimedRewardsString(rewardsWithoutPromo),
                    success: true,
                });
            }

            if (rewardWithPromo) {
                setIsPromoModalOpen(true);
                setPromoModalData({
                    nftUri: rewardWithPromo.nftUri,
                    promoDescriptions: rewardWithPromo.promoDescriptions,
                    nftMintDate: rewardWithPromo.nftMintDate,
                });
            }

            queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] });
            queryClient.invalidateQueries({ queryKey: ["transactions-paginated"] });
            void updateUserLevel();
        } catch (e) {
            // Error claiming quest reward - show user-friendly message
            setClaimingToast({
                message: "There has been a problem claiming your reward. Please try again later!",
                success: false,
            });
            setIsPromoModalOpen(false);
        } finally {
            setIsClaimingInProgress(false);
        }
    }


    if (isLoading) {
        return (
            <div className={styles.questProgressCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <FaTrophy />
                        </span>
                        <h3>Quest Progression</h3>
                    </div>
                </div>
                <Skeleton height={300} rows={1} />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className={styles.questProgressCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <FaTrophy />
                        </span>
                        <h3>Quest Progression</h3>
                    </div>
                </div>
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>🏆</div>
                    <h4>Your Quest Dashboard Awaits!</h4>
                    <p>Sign in to see all active quests, track progress, and claim XP rewards.</p>
                </div>
            </div>
        );
    }

    /*
     * Note: Authenticated users should ALWAYS see filters and game dropdown
     * even if they have no progress yet. They can select a game to see available quests.
     */

    return (
        <div className={`${styles.questProgressCard} ${compact ? styles.compact : ""} ${expanded ? styles.expanded : styles.collapsed}`}>
            <div
                className={styles.header}
                onClick={handleToggle}
                onKeyDown={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls="quest-progress-content"
            >
                <div className={styles.titleGroup}>
                    <span className={styles.iconWrapper}>
                        <FaTrophy />
                    </span>
                    <h3>Quest Progression</h3>
                </div>
                <div className={styles.headerRight}>
                    {summary.claimableCount > 0 &&
            <span className={styles.claimableBadge}>
                {summary.claimableCount} Ready!
            </span>
                    }
                    <button
                        className={styles.collapseToggle}
                        aria-label={expanded ? "Collapse" : "Expand"}
                    >
                        {expanded ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                </div>
            </div>

            <AnimateHeight
                id="quest-progress-content"
                duration={300}
                height={expanded ? "auto" : 0}
            >
                <div className={styles.content}>
                    {/* Filter buttons */}
                    <div className={styles.filterButtons}>
                        <button
                            className={getButtonClassName("filterButton", filter === "in-progress")}
                            onClick={() => setFilter("in-progress")}
                        >
                    Active ({filterCounts["in-progress"]})
                        </button>
                        <button
                            className={getButtonClassName("filterButton", filter === "claimable")}
                            onClick={() => setFilter("claimable")}
                        >
                    Claimable ({filterCounts.claimable})
                        </button>
                        <button
                            className={getButtonClassName("filterButton", filter === "claimed")}
                            onClick={() => setFilter("claimed")}
                        >
                    Claimed ({filterCounts.claimed})
                        </button>

                        {/* Game Filter Dropdown */}
                        {gamesWithQuests.length > 0 &&
          <div className={styles.gameFilterWrapper}>
              <select
                  className={styles.gameFilterDropdown}
                  value={gameFilter}
                  onChange={(e) => setGameFilter(e.target.value)}
              >
                  <option value="all">All Games</option>
                  {gamesWithQuests.map(game =>
                      <option key={game.id} value={game.id}>{game.name}</option>)}
              </select>
          </div>
                        }
                    </div>

                    {/* Time Period Filters - Show for ALL main filters */}
                    <div className={styles.timePeriodFilters}>
                        <button
                            className={getButtonClassName("timePeriodButton", timePeriodFilter === "all")}
                            onClick={() => setTimePeriodFilter("all")}
                        >
                            {isMobile ? `All (${timePeriodCounts.all})` : `All (${timePeriodCounts.all})`}
                        </button>
                        <button
                            className={getButtonClassName("timePeriodButton", timePeriodFilter === "daily")}
                            onClick={() => setTimePeriodFilter("daily")}
                        >
                            {isMobile ? `D (${timePeriodCounts.daily})` : `Daily (${timePeriodCounts.daily})`}
                        </button>
                        <button
                            className={getButtonClassName("timePeriodButton", timePeriodFilter === "weekly")}
                            onClick={() => setTimePeriodFilter("weekly")}
                        >
                            {isMobile ? `W (${timePeriodCounts.weekly})` : `Weekly (${timePeriodCounts.weekly})`}
                        </button>
                        <button
                            className={getButtonClassName("timePeriodButton", timePeriodFilter === "monthly")}
                            onClick={() => setTimePeriodFilter("monthly")}
                        >
                            {isMobile ? `M (${timePeriodCounts.monthly})` : `Monthly (${timePeriodCounts.monthly})`}
                        </button>
                        <button
                            className={getButtonClassName("timePeriodButton", timePeriodFilter === "all-time")}
                            onClick={() => setTimePeriodFilter("all-time")}
                        >
                            {isMobile ? `AT (${timePeriodCounts["all-time"]})` : `All-Time (${timePeriodCounts["all-time"]})`}
                        </button>
                    </div>

                    {/* Mobile/Compact Card View */}
                    {isMobile &&
                <div className={styles.questCards}>
                    {paginatedQuests.map((questData, index) =>
                        <div
                            key={`quest-${questData.rewardId || questData.quest?._id}-${questData.gameId || "global"}-${index}`}
                            className={`${styles.questCard} ${questData.isClaimable ? styles.claimable : ""}`}
                        >
                            {/* Quest Header */}
                            <div className={styles.questCardHeader}>
                                {/* Row 1: Game tag and Reset badge */}
                                <div className={styles.questHeaderRow}>
                                    <span className={styles.gameTag}>
                                        {questData.game?.name || questData.game?.title || getGameNameForQuest(questData.quest)}
                                    </span>
                                    <span className={`${styles.resetPeriod} ${styles[questData.type]}`}>
                                        {getResetPeriodText(questData.type, true)}
                                    </span>
                                </div>
                                {/* Row 2: Quest name (full width) */}
                                <div className={styles.questNameContainer}>
                                    {questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                        <button
                                            className={styles.expandButton}
                                            onClick={() => toggleQuestExpansion(questData.quest.id)}
                                            aria-label={expandedQuests.has(questData.quest.id) ? "Collapse milestones" : "Expand milestones"}
                                        >
                                            {expandedQuests.has(questData.quest.id) ? "▼" : "▶"}
                                        </button>
                                    }
                                    <h4 className={styles.questName}>
                                        {questData.quest.name}
                                    </h4>
                                </div>
                            </div>

                            {/* Quest Progress */}
                            <div className={styles.questCardProgress}>
                                {renderProgressStatus(questData)}
                            </div>

                            {/* Quest Footer with Rewards and Action */}
                            <div className={styles.questCardFooter}>
                                <div className={styles.rewards}>
                                    {questData.xpReward > 0 &&
                    <span className={styles.xpReward}>{questData.xpReward} XP</span>
                                    }
                                    {questData.fuelReward > 0 &&
                    <span className={styles.fuelReward}>{questData.fuelReward} FUEL</span>
                                    }
                                    {questData.otherReward &&
                    <span className={styles.otherReward}>
                        {questData.otherReward.amount && questData.otherReward.amount > 1
                            ? `${questData.otherReward.amount}x `
                            : ""}
                        {questData.otherReward.name}
                    </span>
                                    }
                                    {questData.xpReward <= 0 && questData.fuelReward <= 0 && !questData.otherReward &&
                    <span className={styles.noReward}>No rewards</span>
                                    }
                                </div>
                            </div>

                            {/* Milestone Details (Accordion) */}
                            {expandedQuests.has(questData.quest.id) && questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                <div className={styles.milestonesContainer}>
                                    <div className={styles.milestonesHeader}>
                                        <span className={styles.milestonesTitle}>
                                            Milestones ({questData.quest.requireProgressionOrder ? "Sequential" : "Parallel"})
                                        </span>
                                        <span className={styles.milestonesSubtitle}>
                                            {questData.quest.requireProgressionOrder
                                                ? "Complete in order"
                                                : "Complete in any order"
                                            }
                                        </span>
                                    </div>
                                    <div className={styles.milestonesList}>
                                        {questData.quest.milestones.map((milestone, index) => {
                                            const milestoneProgression = questData.progression?.progression?.[milestone.id];
                                            const displayDivider = milestone?.displayDivider || 1;
                                            const progress = Math.floor((milestoneProgression?.amount || 0) / displayDivider);
                                            const target = Math.floor((milestone.targetAmount || 0) / displayDivider);

                                            // Find the highest milestone index with any progress
                                            const highestMilestoneWithProgress = questData.quest.milestones.reduce((highest, m, idx) => {
                                                const prog = questData.progression?.progression?.[m.id];
                                                return prog && (prog.amount || 0) > 0 ? idx : highest;
                                            }, -1);

                                            // If quest requires order AND we're on a later milestone, earlier milestones are implicitly complete
                                            const isImplicitlyComplete = questData.quest.requireProgressionOrder &&
                                                highestMilestoneWithProgress > index;

                                            // Check if milestone was previously claimed (even if progress shows 0 after reset)
                                            const wasClaimed = !!milestoneProgression?.claimedAt;

                                            const isComplete = milestoneProgression?.isComplete || progress >= target || isImplicitlyComplete || wasClaimed;
                                            const percentage = target > 0 ? Math.min(progress / target * 100, 100) : 0;
                                            // Don't show lock if milestone is complete
                                            const isLocked = !isComplete && questData.quest.requireProgressionOrder && index > 0 &&
                                                !questData.quest.milestones.slice(0, index).every(prevMilestone => {
                                                    const prevProgression = questData.progression?.progression?.[prevMilestone.id];
                                                    const prevWasClaimed = !!prevProgression?.claimedAt;
                                                    return prevProgression?.isComplete || (prevProgression?.amount || 0) >= (prevMilestone.targetAmount || 0) || prevWasClaimed;
                                                });

                                            return (
                                                <div key={milestone.id}>
                                                    <div
                                                        className={`${styles.milestoneItem} ${isComplete ? styles.complete : ""} ${isLocked ? styles.locked : ""}`}
                                                    >
                                                        <div className={styles.milestoneContent}>
                                                            <div className={styles.milestoneHeader}>
                                                                <span className={styles.milestoneName}>
                                                                    {milestone.name || `Milestone ${index + 1}`}
                                                                    {isLocked && <span className={styles.lockIcon}>🔒</span>}
                                                                </span>
                                                            </div>
                                                            {milestone.description &&
                                                                <div className={styles.milestoneDescriptionRow}>
                                                                    <span className={styles.milestoneDescription}>
                                                                        {milestone.description}
                                                                    </span>
                                                                </div>
                                                            }
                                                            {!wasClaimed && !isComplete
                                                                ? <div className={styles.milestoneProgressRow}>
                                                                    <div className={styles.milestoneProgressBar}>
                                                                        <div
                                                                            className={styles.milestoneProgressFill}
                                                                            style={{ width: `${percentage}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className={styles.milestoneProgressText}>
                                                                        {progress}/{target}
                                                                    </span>
                                                                </div>
                                                                : <div className={styles.milestoneStatusRow}>
                                                                    <span className={styles.milestoneCompleteText}>
                                                                        {milestoneProgression?.claimedAt
                                                                            ? `Claimed ${new Intl.DateTimeFormat("en-US", {
                                                                                month: "short",
                                                                                day: "numeric",
                                                                            }).format(new Date(milestoneProgression.claimedAt))}`
                                                                            : "Complete"}
                                                                    </span>
                                                                </div>
                                                            }
                                                            {/* Checkpoint Ladder */}
                                                            {milestone.checkpoints && milestone.checkpoints.length > 0 &&
                                                                <div className={styles.checkpointList}>
                                                                    {milestone.checkpoints.map((checkpoint, cpIndex) => {
                                                                        const cpThreshold = checkpoint.threshold;
                                                                        const cpProgress = milestoneProgression?.amount || 0;
                                                                        const isCheckpointComplete = cpProgress >= cpThreshold;
                                                                        const isCheckpointActive = !isCheckpointComplete && (cpIndex === 0 || cpProgress >= (milestone.checkpoints?.[cpIndex - 1]?.threshold || 0));
                                                                        const isCheckpointLocked = !isCheckpointComplete && !isCheckpointActive;

                                                                        // Get checkpoint rewards - always look up from rewardConfigsRes since backend doesn't populate rewardConfigs
                                                                        const formatReward = (r: { type: string; amount?: number; name?: string }) => {
                                                                            if (r.type === RewardType.XP) return `${r.amount} XP`;
                                                                            if (r.type === RewardType.FUEL) return `${r.amount} Fuel`;
                                                                            return r.name || "";
                                                                        };

                                                                        const getCheckpointRewardText = () => {
                                                                            // First try populated rewardConfigs
                                                                            if (checkpoint.rewardConfigs && checkpoint.rewardConfigs.length > 0) {
                                                                                return checkpoint.rewardConfigs
                                                                                    .map(r => formatReward(r))
                                                                                    .filter(Boolean)
                                                                                    .join(", ");
                                                                            }
                                                                            // Fallback to looking up by IDs
                                                                            if (checkpoint.rewardConfigIds && checkpoint.rewardConfigIds.length > 0 && rewardConfigsRes?.rewards) {
                                                                                return checkpoint.rewardConfigIds.map(id => {
                                                                                    const rc = rewardConfigsRes.rewards.find(r => r.id === id);
                                                                                    if (!rc) return "";
                                                                                    return formatReward(rc);
                                                                                })
                                                                                    .filter(Boolean)
                                                                                    .join(", ");
                                                                            }
                                                                            return "";
                                                                        };

                                                                        const rewardText = getCheckpointRewardText();

                                                                        // Helper to get checkpoint icon (avoiding nested ternary)
                                                                        const getCheckpointIcon = () => {
                                                                            if (isCheckpointComplete) return "✓";
                                                                            if (isCheckpointLocked) return "🔒";
                                                                            return "○";
                                                                        };

                                                                        return (
                                                                            <div
                                                                                key={`cp-${cpThreshold}`}
                                                                                className={`${styles.checkpointItem} ${isCheckpointComplete ? styles.checkpointComplete : ""} ${isCheckpointActive ? styles.checkpointActive : ""} ${isCheckpointLocked ? styles.checkpointLocked : ""}`}
                                                                            >
                                                                                <span className={styles.checkpointIcon}>
                                                                                    {getCheckpointIcon()}
                                                                                </span>
                                                                                <span className={styles.checkpointProgress}>
                                                                                    {Math.min(cpProgress, cpThreshold)}/{cpThreshold}
                                                                                </span>
                                                                                {rewardText &&
                                                                                    <span className={styles.checkpointReward}>
                                                                                        {rewardText}
                                                                                    </span>
                                                                                }
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            }
                                                        </div>
                                                        <div className={styles.milestoneAction}>
                                                            <div className={styles.milestoneRewards}>
                                                                {milestone.rewardConfigIds && milestone.rewardConfigIds.length > 0
                                                                    ? milestone.rewardConfigIds.map((rewardConfigId) => {
                                                                        if (!rewardConfigsRes) return null;
                                                                        const rewardConfig = rewardConfigsRes.rewards.find((reward) => reward.id === rewardConfigId);
                                                                        if (!rewardConfig) return null;

                                                                        // Display rewards as inline badges
                                                                        if (rewardConfig.type === RewardType.XP) {
                                                                            return <span key={rewardConfigId} className={styles.xpReward}>{rewardConfig.amount} XP</span>;
                                                                        }
                                                                        if (rewardConfig.type === RewardType.FUEL) {
                                                                            return <span key={rewardConfigId} className={styles.fuelReward}>{rewardConfig.amount} FUEL</span>;
                                                                        }
                                                                        return <span key={rewardConfigId} className={styles.otherReward}>{rewardConfig.name || rewardConfig.description}</span>;
                                                                    })
                                                                    : null}
                                                            </div>
                                                            <ClaimBadgeButton
                                                                type="milestone"
                                                                questId={questData.questConfig?.id || questData.quest?.id || ""}
                                                                milestoneId={milestone.id}
                                                                isComplete={milestoneProgression?.isComplete || false}
                                                                claimedAt={milestoneProgression?.claimedAt}
                                                                onClaim={claimOverwolfReward}
                                                                isClaimingInProgress={isClaimingInProgress}
                                                                windowWidth={windowWidth || 1920}
                                                                className={styles.cockpitClaimButton}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            }
                        </div>)}
                </div>
                    }

                    {/* Desktop Table View */}
                    {!isMobile &&
                <table className={styles.questTable}>
                    <thead>
                        <tr>
                            <th>Quest</th>
                            <th>Reset</th>
                            <th>Progress</th>
                            <th>Reward</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedQuests.map((questData, index) =>
                            <React.Fragment key={`quest-${questData.rewardId || questData.quest?._id}-${questData.gameId || "global"}-${index}`}>
                                <tr
                                    className={questData.isClaimable ? styles.claimable : ""}
                                >
                                    <td>
                                        <div className={styles.questCell}>
                                            <span className={styles.gameTag}>
                                                {questData.game?.name || questData.game?.title || getGameNameForQuest(questData.quest)}
                                            </span>
                                            <div className={styles.questNameContainer}>
                                                {questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                                    <button
                                                        className={styles.expandButton}
                                                        onClick={() => toggleQuestExpansion(questData.quest.id)}
                                                        aria-label={expandedQuests.has(questData.quest.id) ? "Collapse milestones" : "Expand milestones"}
                                                    >
                                                        {expandedQuests.has(questData.quest.id) ? "▼" : "▶"}
                                                    </button>
                                                }
                                                <span className={styles.questName}>
                                                    {questData.quest.name}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.resetPeriod} ${styles[questData.type]}`}>
                                            {getResetPeriodText(questData.type, false)}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.progressCell}>
                                            {renderProgressStatus(questData)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.rewards}>
                                            {questData.xpReward > 0 &&
                      <span className={styles.xpReward}>{questData.xpReward} XP</span>
                                            }
                                            {questData.fuelReward > 0 &&
                      <span className={styles.fuelReward}>{questData.fuelReward} FUEL</span>
                                            }
                                            {questData.otherReward &&
                      <span className={styles.otherReward}>
                          {questData.otherReward.amount && questData.otherReward.amount > 1
                              ? `${questData.otherReward.amount}x `
                              : ""}
                          {questData.otherReward.name}
                      </span>
                                            }
                                            {questData.xpReward <= 0 && questData.fuelReward <= 0 && !questData.otherReward &&
                      <span className={styles.noReward}>-</span>
                                            }
                                        </div>
                                    </td>
                                    <td>
                                        {renderDesktopActionButton(questData)}
                                    </td>
                                </tr>
                                {/* Expanded Milestones Row */}
                                {expandedQuests.has(questData.quest.id) && questData.quest.milestones && questData.quest.milestones.length > 1 &&
                                <tr className={styles.expandedRow}>
                                    <td colSpan={5} className={styles.expandedCell}>
                                        <div className={styles.milestonesContainer}>
                                            <div className={styles.milestonesHeader}>
                                                <span className={styles.milestonesTitle}>
                                                    Milestones ({questData.quest.requireProgressionOrder ? "Sequential" : "Parallel"})
                                                </span>
                                                <span className={styles.milestonesSubtitle}>
                                                    {questData.quest.requireProgressionOrder
                                                        ? "Complete in order"
                                                        : "Complete in any order"
                                                    }
                                                </span>
                                            </div>
                                            <div className={styles.milestonesList}>
                                                {questData.quest.milestones.map((milestone, index) => {
                                                    const milestoneProgression = questData.progression?.progression?.[milestone.id];
                                                    const displayDivider = milestone?.displayDivider || 1;
                                                    const progress = Math.floor((milestoneProgression?.amount || 0) / displayDivider);
                                                    const target = Math.floor((milestone.targetAmount || 0) / displayDivider);

                                                    // Find the highest milestone index with any progress
                                                    const highestMilestoneWithProgress = questData.quest.milestones.reduce((highest, m, idx) => {
                                                        const prog = questData.progression?.progression?.[m.id];
                                                        return prog && (prog.amount || 0) > 0 ? idx : highest;
                                                    }, -1);

                                                    // If quest requires order AND we're on a later milestone, earlier milestones are implicitly complete
                                                    const isImplicitlyComplete = questData.quest.requireProgressionOrder &&
                                                        highestMilestoneWithProgress > index;

                                                    // Check if milestone was previously claimed (even if progress shows 0 after reset)
                                                    const wasClaimed = !!milestoneProgression?.claimedAt;

                                                    const isComplete = milestoneProgression?.isComplete || progress >= target || isImplicitlyComplete || wasClaimed;
                                                    const percentage = target > 0 ? Math.min(progress / target * 100, 100) : 0;
                                                    // Don't show lock if milestone is complete
                                                    const isLocked = !isComplete && questData.quest.requireProgressionOrder && index > 0 &&
                                                        !questData.quest.milestones.slice(0, index).every(prevMilestone => {
                                                            const prevProgression = questData.progression?.progression?.[prevMilestone.id];
                                                            const prevWasClaimed = !!prevProgression?.claimedAt;
                                                            return prevProgression?.isComplete || (prevProgression?.amount || 0) >= (prevMilestone.targetAmount || 0) || prevWasClaimed;
                                                        });

                                                    return (
                                                        <div key={milestone.id}>
                                                            <div
                                                                className={`${styles.milestoneItem} ${isComplete ? styles.complete : ""} ${isLocked ? styles.locked : ""}`}
                                                            >
                                                                {/* Column 1: Milestone Name (with description below) */}
                                                                <div className={styles.milestoneNameColumn}>
                                                                    <span className={styles.milestoneName}>
                                                                        {milestone.name || `Milestone ${index + 1}`}
                                                                        {isLocked && <span className={styles.lockIcon}>🔒</span>}
                                                                    </span>
                                                                    {milestone.description &&
                                                                        <span className={styles.milestoneDescription}>
                                                                            {milestone.description}
                                                                        </span>
                                                                    }
                                                                </div>

                                                                {/* Column 2: REMOVED - Description now under name */}
                                                                <div className={styles.milestoneResetColumn}>
                                                                    {/* Empty - description moved to Column 1 */}
                                                                </div>

                                                                {/* Column 3: Progress Bar + Text */}
                                                                <div className={styles.milestoneProgressColumn}>
                                                                    {!wasClaimed && !isComplete
                                                                        ? <>
                                                                            <div className={styles.milestoneProgressBar}>
                                                                                <div
                                                                                    className={styles.milestoneProgressFill}
                                                                                    style={{ width: `${percentage}%` }}
                                                                                />
                                                                            </div>
                                                                            <span className={styles.milestoneProgressText}>
                                                                                {progress}/{target}
                                                                            </span>
                                                                        </>
                                                                        : <span className={styles.milestoneCompleteText}>
                                                                            {wasClaimed && milestoneProgression?.claimedAt
                                                                                ? `Claimed ${new Intl.DateTimeFormat("en-US", {
                                                                                    month: "short",
                                                                                    day: "numeric",
                                                                                }).format(new Date(milestoneProgression.claimedAt))}`
                                                                                : "Complete"}
                                                                        </span>
                                                                    }
                                                                </div>

                                                                {/* Column 4: Rewards + Action (combined) */}
                                                                <div className={styles.milestoneRewardActionColumn}>
                                                                    <div className={styles.milestoneRewards}>
                                                                        {milestone.rewardConfigIds && milestone.rewardConfigIds.length > 0
                                                                            ? milestone.rewardConfigIds.map((rewardConfigId) => {
                                                                                if (!rewardConfigsRes) return null;
                                                                                const rewardConfig = rewardConfigsRes.rewards.find((reward) => reward.id === rewardConfigId);
                                                                                if (!rewardConfig) return null;

                                                                                // Display rewards as inline badges
                                                                                if (rewardConfig.type === RewardType.XP) {
                                                                                    return <span key={rewardConfigId} className={styles.xpReward}>{rewardConfig.amount} XP</span>;
                                                                                }
                                                                                if (rewardConfig.type === RewardType.FUEL) {
                                                                                    return <span key={rewardConfigId} className={styles.fuelReward}>{rewardConfig.amount} FUEL</span>;
                                                                                }
                                                                                return <span key={rewardConfigId} className={styles.otherReward}>{rewardConfig.name || rewardConfig.description}</span>;
                                                                            })
                                                                            : null}
                                                                    </div>
                                                                    <ClaimBadgeButton
                                                                        type="milestone"
                                                                        questId={questData.questConfig?.id || questData.quest?.id || ""}
                                                                        milestoneId={milestone.id}
                                                                        isComplete={milestoneProgression?.isComplete || false}
                                                                        claimedAt={milestoneProgression?.claimedAt}
                                                                        onClaim={claimOverwolfReward}
                                                                        isClaimingInProgress={isClaimingInProgress}
                                                                        windowWidth={windowWidth || 1920}
                                                                        className={styles.cockpitClaimButton}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                }
                            </React.Fragment>)}
                    </tbody>
                </table>
                    }

                    {filteredQuests.length === 0 &&
        <div className={styles.noResults}>
            <span>No {filter !== "all" ? filter : ""} quests found</span>
        </div>
                    }

                    {/* Client-side pagination controls */}
                    {clientPagination.totalPages > 1 &&
        <div className={styles.pagination}>
            <button
                className={styles.pageButton}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
            >
                Previous
            </button>
            <span className={styles.pageInfo}>
                Page {clientPagination.page} of {clientPagination.totalPages} ({clientPagination.total} total quests)
            </span>
            <button
                className={styles.pageButton}
                onClick={() => setCurrentPage(p => Math.min(clientPagination.totalPages, p + 1))}
                disabled={currentPage === clientPagination.totalPages}
            >
                Next
            </button>
        </div>
                    }
                </div>
            </AnimateHeight>

            {/* Toast notification */}
            <Toast
                isOpen={showToast}
                onDismiss={() => setShowToast(false)}
                message={toastMessage || ""}
                duration={3000}
                variant={toastMessage?.includes("Failed") ? "error" : "success"}
            />

            {/* Claiming toast (from Mission Tracker pattern) */}
            {claimingToast &&
                <Toast
                    isOpen={!!claimingToast}
                    onDismiss={() => setClaimingToast(null)}
                    message={claimingToast.message}
                    duration={3000}
                    variant={claimingToast.success ? "success" : "error"}
                />
            }

            {/* NFT Promo Modal (for NFT reward claims) - following GoVipOWQuestRewardCard pattern */}
            <NftPromoModal
                loading={!promoModalData}
                dateMinted={new Date(promoModalData?.nftMintDate)}
                isOpen={isPromoModalOpen}
                onClose={() => setIsPromoModalOpen(false)}
                image={promoModalData?.nftUri}
                description={
                    promoModalData?.promoDescriptions.length === 1
                        ? promoModalData?.promoDescriptions[0]
                        : promoModalData?.promoDescriptions.map((desc) => `- ${desc}`).join("\n")
                }
            />
        </div>
    );
};